import React, { useEffect, useState } from "react";
import type { Nullable } from "@poscon/shared-types";
import { useRootSelector } from "~redux/hooks";
import {
  EquipmentTemplateSelectOption,
  type EquipmentTemplateBodyProps,
} from "components/views/template/EquipmentTemplateMenu";
import { includes } from "@poscon/shared-types";
import {  defaultTint, edstFontDim } from "~/utils/constants";
import { edstFontName, getBitmapTextStyles, Label, useInputProps } from "@poscon/shared-frontend";
import { EdstInput } from "~/components/utils/EdstInput";

const tint = defaultTint;

const transponderCategories = ["A", "C", "X", "P", "I", "S", "H", "L", "E"] as const;
type TransponderCat = (typeof transponderCategories)[number];

function isTransponderCat(cat: string): cat is TransponderCat {
  return includes(transponderCategories, cat);
}

const transponderCatText = {
  A: "(MODE A WITH NO MODE C)",
  C: "(MODE A WITH MODE C)",
  X: "(MODE S ONLY)",
  P: "(MODE S & PRESSURE ALTITUDE)",
  I: "(MODE S & ACID TRANSMISSION)",
  S: "(MODE S & ACID & PRESSURE ALTITUDE)",
  H: "(MODE S & ACID TRANSMISSION & ENHANCED)",
  L: "(MODE S & ACID & PRESSURE ALTITUDE & ENHANCED)",
  E: "(MODE S & ACID & PRESSURE ALTITUDE & EXTENDED SQUITTER)",
};

enum AdsbB {
  B1 = "B1",
  B2 = "B2",
}

enum AdsbU {
  U1 = "U1",
  U2 = "U2",
}

enum AdsbV {
  V1 = "V1",
  V2 = "V2",
}

export const EquipmentSurvTemplate = ({ setReset, fp }: EquipmentTemplateBodyProps) => {
  const [transponderCat, setTransponderCat] = useState<Nullable<TransponderCat>>(null);
  const [adsbBCat, setAdsbBCat] = useState<Nullable<AdsbB>>(null);
  const [adsbUCat, setAdsbUCat] = useState<Nullable<AdsbU>>(null);
  const [adsbVCat, setAdsbVCat] = useState<Nullable<AdsbV>>(null);

  const inputProps = useInputProps(
    `eqp/surv/input`,
    "",
    (s) => {
      // TODO: implement
    },
    {
      maxLength: 50,
      focusOnMount: false,
    }
  );

  useEffect(() => {
    // TODO: we need to include more codes here
    const field10b = fp?.surveillanceCodes ?? [];
    const transponderCat = field10b?.[0];

    const transponderCategory = transponderCat && isTransponderCat(transponderCat) ? transponderCat : null;
    const adsbBInitialCat =
      (Object.values(AdsbB).filter((t) => field10b?.includes(t))?.[0] as AdsbB | undefined) ?? null;
    const adsbUInitialCat =
      (Object.values(AdsbU).filter((t) => field10b?.includes(t))?.[0] as AdsbU | undefined) ?? null;
    const adsbVInitialCat =
      (Object.values(AdsbV).filter((t) => field10b?.includes(t))?.[0] as AdsbV | undefined) ?? null;

    const reset = () => {
      setTransponderCat(transponderCategory);
      setAdsbBCat(adsbBInitialCat);
      setAdsbUCat(adsbUInitialCat);
      setAdsbVCat(adsbVInitialCat);
    };

    setReset(reset);
    reset();
  }, [fp?.communicationCodes, setReset]);

  return (
    <>
      <container x={10}>
        <Label text="TRANSPONDER CATEGORY" x={edstFontDim.width * 4} tint={tint} fontName={edstFontName} />
        <container y={edstFontDim.height + 8}>
          <EquipmentTemplateSelectOption
            text="No Transponder"
            selected={transponderCat === null}
            onmousedown={() => setTransponderCat(null)}
          />
          {transponderCategories.map((category, i) => (
            <EquipmentTemplateSelectOption
              y={(edstFontDim.height + 3) * (i + 1)}
              text={`${category} ${transponderCatText[category]}`}
              selected={transponderCat === category}
              onmousedown={() => setTransponderCat(category)}
            />
          ))}
        </container>
        <container y={(edstFontDim.height + 3) * (transponderCategories.length + 2) + 16}>
          <bitmapText text="SUR/" y={4} style={{ ...getBitmapTextStyles(edstFontName), fill: tint }} />
          <EdstInput
            {...inputProps}
            x={Math.floor(edstFontDim.width * 4.5)}
            width={edstFontDim.width * 50 + 4}
          />
        </container>
      </container>
      <container x={edstFontDim.width * 65}>
        <bitmapText
          text="ADS-B"
          x={Math.floor(edstFontDim.width * 6.5)}
          style={{ ...getBitmapTextStyles(edstFontName), fill: tint }}
        />
        <Label text="CATEGORY" x={edstFontDim.width * 5} y={edstFontDim.height} tint={tint} fontName={edstFontName} />
        <container y={edstFontDim.height * 2 + 8}>
          <EquipmentTemplateSelectOption
            text="No 1090"
            selected={adsbBCat === null}
            onmousedown={() => setAdsbBCat(null)}
          />
          <EquipmentTemplateSelectOption
            y={edstFontDim.height + 3}
            text="B1 (1090 OUT)"
            selected={adsbBCat === AdsbB.B1}
            onmousedown={() => setAdsbBCat(AdsbB.B1)}
          />
          <EquipmentTemplateSelectOption
            y={(edstFontDim.height + 3) * 2}
            text="B2 (1090 IN/OUT)"
            selected={adsbBCat === AdsbB.B2}
            onmousedown={() => setAdsbBCat(AdsbB.B2)}
          />
          <container y={(edstFontDim.height + 3) * 3 + 12}>
            <EquipmentTemplateSelectOption
              text="No UAT"
              selected={adsbUCat === null}
              onmousedown={() => setAdsbUCat(null)}
            />
            <EquipmentTemplateSelectOption
              y={edstFontDim.height + 3}
              text="U1 (UAT OUT)"
              selected={adsbUCat === AdsbU.U1}
              onmousedown={() => setAdsbUCat(AdsbU.U1)}
            />
            <EquipmentTemplateSelectOption
              y={(edstFontDim.height + 3) * 2}
              text="U2 (UAT IN/OUT)"
              selected={adsbUCat === AdsbU.U2}
              onmousedown={() => setAdsbUCat(AdsbU.U2)}
            />
          </container>
          <container y={(edstFontDim.height + 3) * 6 + 24}>
            <EquipmentTemplateSelectOption
              text="No VDL"
              selected={adsbVCat === null}
              onmousedown={() => setAdsbVCat(null)}
            />
            <EquipmentTemplateSelectOption
              y={edstFontDim.height + 3}
              text="V1 (VDL OUT)"
              selected={adsbVCat === AdsbV.V1}
              onmousedown={() => setAdsbVCat(AdsbV.V1)}
            />
            <EquipmentTemplateSelectOption
              y={(edstFontDim.height + 3) * 2}
              text="V2 (VDL IN/OUT)"
              selected={adsbVCat === AdsbV.V2}
              onmousedown={() => setAdsbVCat(AdsbV.V2)}
            />
          </container>
        </container>
      </container>
      <container x={edstFontDim.width * 85}>
        <bitmapText
          text="ADS-B"
          x={edstFontDim.width * 4}
          style={{ ...getBitmapTextStyles(edstFontName), fill: tint }}
        />
        <Label text="CERTIFICATION" x={0} y={edstFontDim.height} tint={tint} fontName={edstFontName} />
        <EquipmentTemplateSelectOption
          text="260B (1090)"
          y={(edstFontDim.height + 3) * 3 + 8}
          selected={false}
          onmousedown={() => {}}
        />
        <EquipmentTemplateSelectOption
          text="282B (UAT)"
          y={(edstFontDim.height + 3) * 6 + 8}
          selected={false}
          onmousedown={() => {}}
        />
      </container>
    </>
  );
};
